import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { UpdateVehicleInput, VehicleDetail } from 'used-vehicle-contracts';
import { orpc } from '../../../apis/orpc-query';
import { Badge } from '../../../components/ui/Badge';
import { Button } from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import { DefinitionList } from '../../../components/ui/DefinitionList';
import { FormField, FormSection } from '../../../components/ui/FormField';
import { Input } from '../../../components/ui/Input';
import { Select } from '../../../components/ui/Select';
import { Sheet } from '../../../components/ui/Sheet';
import { formatDay, formatKm, formatMoney, formatText } from '../../../lib/format';
import { serverMessage, submitErrorKey } from '../../../lib/form-helpers';
import { useI18nTranslation } from '../../../lib/use-i18n-translation';
import { FUELS, ORIGINS, SERVICE_HISTORIES, toOptionalNumber, toOptionalText, USAGES } from '../../estimates/estimates-display';
import { EQUIPMENT_OPTIONS } from '../vehicles-display';

export interface VehicleOverviewTabProps {
  vehicle: VehicleDetail;
  editable: boolean;
  onChanged: () => Promise<void>;
}

interface IdentityForm {
  make: string;
  model: string;
  trim: string;
  modelYear: string;
  odometerKm: string;
  ownerCount: string;
  vin: string;
  plate: string;
  color: string;
  fuel: string;
  drivetrain: string;
  transmission: string;
  origin: string;
  usage: string;
  serviceHistory: string;
  registrationExpiry: string;
  storageLocation: string;
  equipment: string[];
}

const formFrom = (vehicle: VehicleDetail): IdentityForm => ({
  make: vehicle.make ?? '',
  model: vehicle.model ?? '',
  trim: vehicle.trim ?? '',
  modelYear: vehicle.modelYear === null ? '' : String(vehicle.modelYear),
  odometerKm: vehicle.odometerKm === null ? '' : String(vehicle.odometerKm),
  ownerCount: vehicle.ownerCount === null ? '' : String(vehicle.ownerCount),
  vin: vehicle.vin ?? '',
  plate: vehicle.plate ?? '',
  color: vehicle.color ?? '',
  fuel: vehicle.fuel,
  drivetrain: vehicle.drivetrain ?? '',
  transmission: vehicle.transmission ?? '',
  origin: vehicle.origin ?? '',
  usage: vehicle.usage ?? '',
  serviceHistory: vehicle.serviceHistory,
  registrationExpiry: vehicle.registrationExpiry ?? '',
  storageLocation: vehicle.storageLocation ?? '',
  equipment: [...vehicle.equipment],
});

/**
 * Overview — the dossier's first page (plan §7).
 *
 * It reads like a record rather than a form: the facts are a `DefinitionList`, and the pencil opens the
 * **IdentitySheet**, which is the only place they change. That is deliberate. A purchased vehicle's
 * identity is what the price, the paperwork and the eventual sale all rest on, so editing it should feel
 * like a decision, not like tabbing through a grid.
 */
export const VehicleOverviewTab = ({ vehicle, editable, onChanged }: VehicleOverviewTabProps) => {
  const { t } = useI18nTranslation();
  const [isOpen, setOpen] = useState(false);
  const [form, setForm] = useState<IdentityForm>(() => formFrom(vehicle));
  const [error, setError] = useState<string | null>(null);

  const optionsQuery = useQuery(orpc.vehicles.attributeOptions.queryOptions());
  const update = useMutation(orpc.vehicles.update.mutationOptions());

  useEffect(() => {
    if (isOpen) {
      setForm(formFrom(vehicle));
      setError(null);
    }
  }, [isOpen, vehicle]);

  const change = (patch: Partial<IdentityForm>) => setForm((current) => ({ ...current, ...patch }));

  const save = async () => {
    setError(null);

    const patch: UpdateVehicleInput = {
      vehicleId: vehicle.id,
      expectedRevision: vehicle.revision,
      make: toOptionalText(form.make),
      model: toOptionalText(form.model),
      trim: toOptionalText(form.trim),
      modelYear: toOptionalNumber(form.modelYear),
      odometerKm: toOptionalNumber(form.odometerKm),
      ownerCount: toOptionalNumber(form.ownerCount),
      vin: toOptionalText(form.vin),
      plate: toOptionalText(form.plate),
      color: toOptionalText(form.color),
      fuel: form.fuel as VehicleDetail['fuel'],
      drivetrain: toOptionalText(form.drivetrain),
      transmission: toOptionalText(form.transmission),
      origin: (toOptionalText(form.origin) as VehicleDetail['origin']) ?? null,
      usage: (toOptionalText(form.usage) as VehicleDetail['usage']) ?? null,
      serviceHistory: form.serviceHistory as VehicleDetail['serviceHistory'],
      registrationExpiry: toOptionalText(form.registrationExpiry),
      storageLocation: toOptionalText(form.storageLocation),
      equipment: form.equipment,
    };

    try {
      await update.mutateAsync(patch);
      await onChanged();
      setOpen(false);
    } catch (caught: unknown) {
      setError(serverMessage(caught) ?? t(submitErrorKey(caught)));
    }
  };

  const identity = [
    { label: t('estimates.make'), value: formatText(vehicle.make) },
    { label: t('estimates.model'), value: formatText(vehicle.model) },
    { label: t('estimates.trim'), value: formatText(vehicle.trim) },
    { label: t('estimates.modelYear'), value: vehicle.modelYear ?? t('common.notSet') },
    { label: t('estimates.odometerKm'), value: formatKm(vehicle.odometerKm) },
    { label: t('estimates.ownerCount'), value: vehicle.ownerCount ?? t('common.notSet') },
    { label: t('estimates.vin'), value: <span className="font-mono">{formatText(vehicle.vin)}</span> },
    { label: t('vehicles.plate'), value: formatText(vehicle.plate) },
    { label: t('vehicles.color'), value: formatText(vehicle.color) },
    { label: t('estimates.fuel'), value: t(`enums.fuel.${vehicle.fuel}`) },
    { label: t('vehicles.drivetrain'), value: formatText(vehicle.drivetrain) },
    { label: t('vehicles.transmission'), value: formatText(vehicle.transmission) },
    { label: t('estimates.origin'), value: vehicle.origin === null ? t('common.notSet') : t(`enums.origin.${vehicle.origin}`) },
    { label: t('estimates.usage'), value: vehicle.usage === null ? t('common.notSet') : t(`enums.usage.${vehicle.usage}`) },
    { label: t('estimates.serviceHistory'), value: t(`enums.serviceHistory.${vehicle.serviceHistory}`) },
    { label: t('estimates.registrationExpiry'), value: formatDay(vehicle.registrationExpiry) },
  ];

  const provenance = [
    {
      label: t('vehicles.seller'),
      value: vehicle.seller === null ? t('common.notRecorded') : `${vehicle.seller.name} · ${vehicle.seller.phone}`,
    },
    { label: t('vehicles.assignee'), value: formatText(vehicle.assignedUserName) },
    { label: t('vehicles.storageLocation'), value: formatText(vehicle.storageLocation) },
    { label: t('vehicles.purchasePrice'), value: formatMoney(vehicle.purchasePrice) },
    { label: t('vehicles.purchaseDate'), value: formatDay(vehicle.purchaseDate) },
    { label: t('vehicles.daysInStock'), value: vehicle.daysInStock ?? t('common.notSet') },
    {
      label: t('vehicles.sourceEstimate'),
      value:
        vehicle.sourceEstimate === null ? (
          t('vehicles.noSourceEstimate')
        ) : (
          <Link
            to={`/estimates/${vehicle.sourceEstimate.id}`}
            className="font-mono underline"
          >
            {vehicle.sourceEstimate.estimateNo}
          </Link>
        ),
    },
  ];

  return (
    <div className="space-y-4">
      <section className="space-y-4 rounded-lg border border-border bg-card p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-sm font-semibold text-card-foreground">{t('vehicles.identityTitle')}</h2>
            <p className="mt-1 text-sm text-muted-foreground">{t('vehicles.identityBody')}</p>
          </div>
          {editable ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setOpen(true)}
            >
              {t('actions.edit')}
            </Button>
          ) : null}
        </div>
        <DefinitionList items={identity} />
      </section>

      <section className="space-y-4 rounded-lg border border-border bg-card p-5">
        <h2 className="text-sm font-semibold text-card-foreground">{t('vehicles.provenanceTitle')}</h2>
        <DefinitionList items={provenance} />
      </section>

      <section className="space-y-3 rounded-lg border border-border bg-card p-5">
        <h2 className="text-sm font-semibold text-card-foreground">{t('vehicles.equipmentTitle')}</h2>
        <p className="text-sm text-muted-foreground">{t('vehicles.equipmentBody')}</p>
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {EQUIPMENT_OPTIONS.map((option) => (
            <Checkbox
              key={option}
              id={`equipment-${option}`}
              label={t(`vehicles.equipment.${option}`, { defaultValue: option })}
              checked={vehicle.equipment.includes(option)}
              disabled
              readOnly
            />
          ))}
        </div>
        {vehicle.equipment.length === 0 ? <Badge>{t('vehicles.equipmentNone')}</Badge> : null}
      </section>

      <Sheet
        isOpen={isOpen}
        onClose={() => setOpen(false)}
        title={t('vehicles.identitySheetTitle')}
        description={t('vehicles.identitySheetBody')}
        footer={
          <>
            <Button
              variant="outline"
              onClick={() => setOpen(false)}
            >
              {t('actions.cancel')}
            </Button>
            <Button
              isLoading={update.isPending}
              onClick={() => void save()}
            >
              {t('actions.save')}
            </Button>
          </>
        }
      >
        <div className="space-y-5">
          {error === null ? null : (
            <p
              role="alert"
              className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive"
            >
              {error}
            </p>
          )}

          <FormSection title={t('vehicles.identityTitle')}>
            <div className="grid gap-4 sm:grid-cols-2">
              {(
                [
                  ['make', t('estimates.make')],
                  ['model', t('estimates.model')],
                  ['trim', t('estimates.trim')],
                  ['vin', t('estimates.vin')],
                  ['plate', t('vehicles.plate')],
                  ['color', t('vehicles.color')],
                  ['drivetrain', t('vehicles.drivetrain')],
                  ['transmission', t('vehicles.transmission')],
                ] as const
              ).map(([key, label]) => (
                <FormField
                  key={key}
                  id={`vehicle-${key}`}
                  label={label}
                >
                  <Input
                    id={`vehicle-${key}`}
                    list={key === 'color' || key === 'drivetrain' || key === 'transmission' ? `vehicle-${key}-options` : undefined}
                    value={form[key]}
                    onChange={(event) => change({ [key]: event.target.value })}
                  />
                </FormField>
              ))}

              {(
                [
                  ['modelYear', t('estimates.modelYear')],
                  ['odometerKm', t('estimates.odometerKm')],
                  ['ownerCount', t('estimates.ownerCount')],
                ] as const
              ).map(([key, label]) => (
                <FormField
                  key={key}
                  id={`vehicle-${key}`}
                  label={label}
                >
                  <Input
                    id={`vehicle-${key}`}
                    type="number"
                    value={form[key]}
                    onChange={(event) => change({ [key]: event.target.value })}
                  />
                </FormField>
              ))}

              <FormField
                id="vehicle-fuel"
                label={t('estimates.fuel')}
              >
                <Select
                  id="vehicle-fuel"
                  value={form.fuel}
                  onChange={(event) => change({ fuel: event.target.value })}
                  options={FUELS.map((fuel) => ({ value: fuel, label: t(`enums.fuel.${fuel}`) }))}
                />
              </FormField>

              <FormField
                id="vehicle-origin"
                label={t('estimates.origin')}
              >
                <Select
                  id="vehicle-origin"
                  value={form.origin}
                  placeholder={t('common.notSet')}
                  onChange={(event) => change({ origin: event.target.value })}
                  options={ORIGINS.map((origin) => ({ value: origin, label: t(`enums.origin.${origin}`) }))}
                />
              </FormField>

              <FormField
                id="vehicle-usage"
                label={t('estimates.usage')}
              >
                <Select
                  id="vehicle-usage"
                  value={form.usage}
                  placeholder={t('common.notSet')}
                  onChange={(event) => change({ usage: event.target.value })}
                  options={USAGES.map((usage) => ({ value: usage, label: t(`enums.usage.${usage}`) }))}
                />
              </FormField>

              <FormField
                id="vehicle-service-history"
                label={t('estimates.serviceHistory')}
              >
                <Select
                  id="vehicle-service-history"
                  value={form.serviceHistory}
                  onChange={(event) => change({ serviceHistory: event.target.value })}
                  options={SERVICE_HISTORIES.map((history) => ({ value: history, label: t(`enums.serviceHistory.${history}`) }))}
                />
              </FormField>

              <FormField
                id="vehicle-registration-expiry"
                label={t('estimates.registrationExpiry')}
              >
                <Input
                  id="vehicle-registration-expiry"
                  type="date"
                  dir="ltr"
                  value={form.registrationExpiry}
                  onChange={(event) => change({ registrationExpiry: event.target.value })}
                />
              </FormField>
            </div>
          </FormSection>

          <FormSection title={t('vehicles.storageTitle')}>
            <FormField
              id="vehicle-storage-location"
              label={t('vehicles.storageLocation')}
            >
              <Input
                id="vehicle-storage-location"
                list="vehicle-storage-options"
                value={form.storageLocation}
                onChange={(event) => change({ storageLocation: event.target.value })}
              />
            </FormField>
          </FormSection>

          <FormSection title={t('vehicles.equipmentTitle')}>
            <div className="grid gap-2 sm:grid-cols-2">
              {EQUIPMENT_OPTIONS.map((option) => (
                <Checkbox
                  key={option}
                  id={`vehicle-equipment-${option}`}
                  label={t(`vehicles.equipment.${option}`, { defaultValue: option })}
                  checked={form.equipment.includes(option)}
                  onChange={(event) =>
                    change({
                      equipment: event.target.checked ? [...form.equipment, option] : form.equipment.filter((entry) => entry !== option),
                    })
                  }
                />
              ))}
            </div>
          </FormSection>

          {/* Suggestions drawn from what this workspace already records, so two people spell one colour
              the same way — never a controlled vocabulary that refuses a new value. */}
          <datalist id="vehicle-color-options">
            {(optionsQuery.data?.colors ?? []).map((value) => (
              <option
                key={value}
                value={value}
              />
            ))}
          </datalist>
          <datalist id="vehicle-drivetrain-options">
            {(optionsQuery.data?.drivetrains ?? []).map((value) => (
              <option
                key={value}
                value={value}
              />
            ))}
          </datalist>
          <datalist id="vehicle-transmission-options">
            {(optionsQuery.data?.transmissions ?? []).map((value) => (
              <option
                key={value}
                value={value}
              />
            ))}
          </datalist>
          <datalist id="vehicle-storage-options">
            {(optionsQuery.data?.storageLocations ?? []).map((value) => (
              <option
                key={value}
                value={value}
              />
            ))}
          </datalist>
        </div>
      </Sheet>
    </div>
  );
};
